import { unstable_batchedUpdates } from 'react-dom';

/* eslint-disable @typescript-eslint/no-explicit-any */
export const throttleRAF = <T extends any[]>(
  fn: (...args: T) => void,
  opts?: { trailing?: boolean },
) => {
  let timerId: number | undefined;
  let lastArgs: T | undefined;
  let lastArgsTrailing: T | undefined;

  const scheduleFunc = (args: T) => {
    timerId = window.requestAnimationFrame(() => {
      timerId = undefined;
      fn(...args);
      lastArgs = undefined;
      if (lastArgsTrailing) {
        lastArgs = lastArgsTrailing;
        lastArgsTrailing = undefined;
        scheduleFunc(lastArgs);
      }
    });
  };

  const ret = (...args: T) => {
    lastArgs = args;
    if (timerId === undefined) {
      scheduleFunc(lastArgs);
    } else if (opts?.trailing) {
      lastArgsTrailing = args;
    }
  };

  ret.flush = () => {
    if (timerId !== undefined) {
      cancelAnimationFrame(timerId);
      timerId = undefined;
    }

    if (lastArgs) {
      fn(...(lastArgsTrailing || lastArgs));
      lastArgs = lastArgsTrailing = undefined;
    }
  };

  ret.cancel = () => {
    lastArgs = lastArgsTrailing = undefined;
    if (timerId !== undefined) {
      cancelAnimationFrame(timerId);
      timerId = undefined;
    }
  };

  return ret;
};

export const withThrottled = <
  TFunction extends (() => void) | ((event: any) => void),
>(
  func: Parameters<TFunction>['length'] extends 0 | 1 ? TFunction : never,
) => {
  // @ts-expect-error Types are not necessary here
  return throttleRAF<Parameters<TFunction>>(((event) => {
    unstable_batchedUpdates(func, event);
  }) as TFunction);
};
